import { DesignSystem } from '@/src/design-system'
import { MesaFooter } from '@/src/common/components/MesaFooter'
import { PartnerDisclosures } from '@/src/common/components/PartnerDisclosures'
import { useContentCards } from '@/src/services/Braze/useContentCards'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { useWindowDimensions, View, type ViewStyle } from 'react-native'
import { useMediaQuery } from 'react-responsive'
import { CardApplication, Experiences } from '@/src/experiences'
import * as Mortgage from '@/src/experiences/mortgage'
import { useActiveApplication } from '@/src/experiences/card-application/hooks'
import { useSourcePartnerName } from '@/src/common/hooks/useSourcePartnerName'
import { ActivateCardButton } from '@/src/common/components/ActivateCardButton'
import { Fragment } from 'react/jsx-runtime'
import { CardDeliveryStatus } from '@/src/common/components/CardDeliveryStatus'
// Refer a Friend temporarily hidden - see https://mesamember.slack.com/archives/C07JK5U3H8S/p1764867423105289
// import { ReferAFriendButton } from '@/src/common/components/Referrals/ReferAFriendButton'
import { CardSection } from '@/src/common/modules/CardSection'
import { MortgageModule } from '@/src/common/modules/MortgageModule'
import { OnboardingChecklist } from '@/src/common/modules/OnboardingChecklist'
import { MainWrapper } from '@/src/common/wrappers/MainWrapper'
import * as CardAccounts from '@/src/experiences/card-accounts'
import { HomeE2E } from '@/src/experiences/home/e2e'
import * as Components from '@/src/experiences/home/components'
import * as Statsig from '@/src/experiences/home/statsig'
import { Api } from '@/src/store/api'
import type { ReactNode } from 'react'

type CardAccountId =
  (typeof CardAccounts.Stores.CardAccountsApi.endpoints)['GET /card/accounts']['Types']['ResultType'][number]['id']

function useMinDimensions() {
  const isSplitScreen = useMediaQuery({ minWidth: 720 })
  const windowDimensions = useWindowDimensions()

  return {
    isSplitScreen,
    minWidth: (isSplitScreen ? '42%' : '88%') satisfies ViewStyle['minWidth'],
    minHeight: (windowDimensions.height / (isSplitScreen ? 2 : 3)) satisfies ViewStyle['minHeight'],
  } as const
}

export function HomeIndex({ cardAccountId }: { cardAccountId?: CardAccountId }) {
  const minDimensions = useMinDimensions()

  return cardAccountId ? (
    <HomeIndexWithCardAccount cardAccountId={cardAccountId} minDimensions={minDimensions} />
  ) : (
    <HomeIndexBase minDimensions={minDimensions} />
  )
}

function HomeIndexWithCardAccount({
  cardAccountId,
  minDimensions,
}: {
  cardAccountId: CardAccountId
  minDimensions: ReturnType<typeof useMinDimensions>
}) {
  const cardInTransitUiGate = useFeatureGate(FeatureGates.CardInTransitUiEnabled)

  const cardShipmentQuery = Api.endpoints['GET /card/:cardAccountId/shipment'].useQuery({
    params: { cardAccountId },
  })

  return (
    <HomeIndexBase
      loading={
        cardInTransitUiGate.loadingStatus === 'Uninitialized' ||
        cardShipmentQuery.isUninitialized ||
        cardShipmentQuery.isLoading
      }
      minDimensions={minDimensions}
      onboardingChecklistModule={<OnboardingChecklist style={{ flex: 1, ...minDimensions }} />}
      cardShipmentModule={
        !cardShipmentQuery.data || cardShipmentQuery.data.status === 'ACTIVE'
          ? undefined
          : (cardShipmentQuery.data.status satisfies
              | 'PENDING_ACTIVATION'
              | 'PENDING_FULFILLMENT'
              | null
              | undefined,
            (
              <>
                <CardDeliveryStatus
                  trackingNumber={cardShipmentQuery.data.trackingNumber}
                  status={(() => {
                    switch (cardShipmentQuery.data.status) {
                      case 'PENDING_FULFILLMENT':
                        return 'PRINTED'
                      case 'PENDING_ACTIVATION':
                        return 'SHIPPED'
                      default:
                        return 'SUBMITTED'
                    }
                  })()}
                  style={{ width: '100%' }}
                />
                {cardShipmentQuery.data.status === 'PENDING_ACTIVATION' && (
                  <ActivateCardButton style={{ width: '100%' }} />
                )}
              </>
            ))
      }
    />
  )
}

function HomeIndexBase({
  loading,
  minDimensions: { minWidth, minHeight, isSplitScreen },
  onboardingChecklistModule,
  cardShipmentModule,
}: {
  loading?: boolean
  minDimensions: ReturnType<typeof useMinDimensions>
  onboardingChecklistModule?: ReactNode
  cardShipmentModule?: ReactNode
}) {
  const activeApplicationQuery = useActiveApplication()

  const isCardIssued = (() => {
    switch (activeApplicationQuery.status) {
      case 'LOADING':
      case 'ERROR':
        return false
      default:
        switch (activeApplicationQuery.activeApplicationStatus) {
          case 'ISSUED':
          case 'APPROVAL_ACKED':
            return true
        }
        return false
    }
  })()

  const mortgageModuleBehavior = Mortgage.Hooks.useMortgageModuleStateBehavior()
  const useNewMortgageSectionFeatureGate = useFeatureGate(Statsig.FeatureGate.UseNewMortgageSection)
  const { sourcePartnerName, loading: loadingSourcePartnerName } = useSourcePartnerName()

  const loadingMortgageSection =
    mortgageModuleBehavior === 'loading' ||
    mortgageModuleBehavior === 'uninitialized' ||
    useNewMortgageSectionFeatureGate.loadingStatus !== 'Ready'

  // The list of partners are: https://github.com/Acre-Rewards/acre-rewards-api/blob/ab36f64781c9f98dec414bcf850bd821d1898b1a/prisma/schema.prisma#L84
  // Note: not all partners are closed loop, i.e. has a mortgage product we want to drive back their customers to.
  // If the parameter store, mesa_mortgage_module has a key-value for the partner name, then we want to display the mortgage module.
  const isClosedLoopPartner = sourcePartnerName !== 'default'

  const shouldShowMortgageSection =
    ((!loadingMortgageSection && mortgageModuleBehavior.type !== 'hide') ||
      (!loadingMortgageSection && isClosedLoopPartner)) &&
    !useNewMortgageSectionFeatureGate.featureGate.value

  const modulesThatCanBeHalfWidth = [
    ...(isCardIssued
      ? onboardingChecklistModule
        ? [<Fragment key="onboarding-checklist">{onboardingChecklistModule}</Fragment>]
        : []
      : [<CardSection style={{ flex: 1, minHeight }} key="card-section" />]),
    ...(shouldShowMortgageSection
      ? [<MortgageModule style={{ flex: 1, minWidth, minHeight }} key="mortgage-module" />]
      : []),
    ...(useNewMortgageSectionFeatureGate.featureGate?.value === true
      ? [
          <View style={{ flex: 1, minWidth, minHeight }} key="mortgage-section">
            <Components.MortgageSection />
          </View>,
        ]
      : []),
  ] as const

  const contentCards = useContentCards('home')
  const contentCardModules = contentCards.map((contentCard) => (
    <DesignSystem.BrazeContentCard
      key={contentCard.id}
      minWidth={minWidth}
      contentCard={contentCard}
      style={{ alignSelf: isSplitScreen ? 'flex-start' : undefined }}
    />
  ))

  const modules = [
    ...(cardShipmentModule
      ? [<Fragment key="card-delivery-status-module">{cardShipmentModule}</Fragment>]
      : []),
    ...contentCardModules,
    ...modulesThatCanBeHalfWidth,
  ] as const

  // Disclosures for co-branded partners
  const parsedResult = Mortgage.Statsig.Store.MortgageModuleVariantName.safeParse(sourcePartnerName)
  const parsedSourcePartnerName = parsedResult.success ? parsedResult.data : 'default'
  const moduleDesign = Mortgage.Statsig.Store.useMortgageModuleDesign(parsedSourcePartnerName)
  const { disclosure_paragraphs } = moduleDesign

  return (
    <MainWrapper
      screen="/main/home"
      footer={
        <>
          {disclosure_paragraphs && disclosure_paragraphs.length > 0 && (
            <PartnerDisclosures disclosureParagraphs={disclosure_paragraphs} />
          )}
          <MesaFooter style={{ width: '100%' }} />
        </>
      }
      scrollViewProps={{ testID: 'Main.Home.ScrollView' }}
      testID={HomeE2E.Ids.View.HomeIndex}
    >
      <Experiences.Notifications.Components.EnablePermissionsBanner text="Always stay up to date with Mesa's card and mortgage rewards!" />
      {/* <ReferAFriendButton style={{ width: '100%' }} sourcePartnerName={sourcePartnerName} /> */}
      <CardApplication.Components.ApplicationStatusBanner />
      {loading ||
      activeApplicationQuery.status === 'LOADING' ||
      loadingMortgageSection ||
      loadingSourcePartnerName
        ? // @todo implement shimmer/skeleton for each module instead of blocking all modules on all loading states
          [1, 2].map((_, index) => (
            <DesignSystem.Skeleton.Skeleton
              key={index}
              style={{
                width: 'auto',
                flex: 1,
                minWidth,
                borderRadius: 16,
                height: minHeight,
                overflow: 'hidden',
              }}
            />
          ))
        : modules}
    </MainWrapper>
  )
}
